import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface RatingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onJobArchived?: () => void;
  jobId: string;
  revieweeId: string;
  revieweeName: string;
  revieweeType: 'customer' | 'tradesman';
}

export default function RatingDialog({ 
  isOpen, 
  onClose, 
  onJobArchived,
  jobId, 
  revieweeId, 
  revieweeName, 
  revieweeType 
}: RatingDialogProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Rating Required",
        description: "Please select a rating before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    console.log('Rating submission started:', { jobId, revieweeId, rating, feedback });

    try {
      // Get current user's profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: currentProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();

      if (!currentProfile) throw new Error("Profile not found");

      console.log('Current profile found:', currentProfile.id);
      console.log('Attempting to insert rating:', {
        job_id: jobId,
        reviewer_id: currentProfile.id,
        reviewee_id: revieweeId,
        rating,
        written_feedback: feedback.trim() || null,
      });

      // Submit rating
      const { error } = await supabase
        .from("ratings")
        .insert({
          job_id: jobId,
          reviewer_id: currentProfile.id,
          reviewee_id: revieweeId,
          rating,
          written_feedback: feedback.trim() || null,
        });

      if (error) throw error;

      // Check if both parties have now rated each other
      const { data: allRatings } = await supabase
        .from("ratings")
        .select("reviewer_id, reviewee_id")
        .eq("job_id", jobId);

      if (allRatings && allRatings.length >= 2) {
        // Both parties have rated - auto-archive the job for both parties
        const { data: jobData } = await supabase
          .from("jobs")
          .select("customer_id, tradesman_id")
          .eq("id", jobId)
          .single();

        if (jobData?.customer_id && jobData?.tradesman_id) {
          // Archive for current user only via secure RPC; DB trigger handles mutual archiving
          try {
            await supabase.rpc('archive_job_for_current_user', {
              p_job_id: jobId,
              p_reason: 'Both parties completed feedback',
            });
          } catch (archiveErr) {
            console.warn('Archive RPC after rating failed (non-fatal):', archiveErr);
          }
          console.log("Archive requested after both parties completed ratings");
          // Immediately notify parent to refresh job list
          onJobArchived?.();
        }
      }

      toast({
        title: "Rating Submitted",
        description: `Thank you for rating ${revieweeName}!`,
      });

      onClose();
      setRating(0);
      setFeedback("");
    } catch (error) {
      console.error("Error submitting rating:", error);
      console.error("Error details:", error);
      toast({
        title: "Error",
        description: "Failed to submit rating. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rate {revieweeName}</DialogTitle>
          <DialogDescription>
            Share your experience working with this {revieweeType}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Star Rating */}
          <div className="space-y-2">
            <Label>Rating</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="p-1 hover:scale-110 transition-transform"
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= (hoveredRating || rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-muted-foreground">
                {rating === 1 && "Poor"}
                {rating === 2 && "Fair"}
                {rating === 3 && "Good"}
                {rating === 4 && "Very Good"}
                {rating === 5 && "Excellent"}
              </p>
            )}
          </div>

          {/* Written Feedback */}
          <div className="space-y-2">
            <Label htmlFor="feedback">Written Feedback (Optional)</Label>
            <Textarea
              id="feedback"
              placeholder={`Share details about your experience with ${revieweeName}...`}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {feedback.length}/500 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || rating === 0}>
              {isSubmitting ? "Submitting..." : "Submit Rating"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}